import { useState } from 'react';
import { intelligenceAPI, QualificationTemplate, ConnectionCompany } from '../../lib/api';
import { assignableTemplates, supplierPickerLabel, templateTypeLabel } from '../../lib/questionnaireLabels';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

interface AssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templates: QualificationTemplate[];
  suppliers: ConnectionCompany[];
  /** True while the supplier list is being fetched. */
  suppliersLoading?: boolean;
  onAssigned: () => void;
}

/* Assign Qualification Dialog */
export default function AssignDialog({
  open,
  onOpenChange,
  templates,
  suppliers,
  suppliersLoading = false,
  onAssigned,
}: AssignDialogProps) {
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [assignNotes, setAssignNotes] = useState('');
  const [assigning, setAssigning] = useState(false);
  // Audit Lite templates are completed by the customer's own auditor — never
  // sent to a supplier (the backend refuses them too).
  // Only published templates go to suppliers — never a draft or an archived one.
  const offered = assignableTemplates(templates).filter((t) => t.status === 'active');
  const supplierOptions = suppliers.map((s) => ({ value: s.id.toString(), label: supplierPickerLabel(s) }));
  const supplierPlaceholder = suppliersLoading
    ? 'Loading suppliers...'
    : suppliers.length === 0 ? 'No suppliers available' : 'Select supplier';

  const handleAssignQualification = async () => {
    if (!selectedTemplate || !selectedSupplier) {
      toast.error('Please select both template and supplier');
      return;
    }

    try {
      setAssigning(true);
      const response = await intelligenceAPI.createQualification({
        template: selectedTemplate,
        supplier: selectedSupplier,
        customer: user?.company,
        status: 'invited',
        notes: assignNotes,
      });

      // Send invitation
      await intelligenceAPI.inviteSupplier(response.data.id);

      toast.success('Questionnaire assigned and invitation sent');
      onOpenChange(false);
      setSelectedTemplate(null);
      setSelectedSupplier(null);
      setAssignNotes('');
      onAssigned();
    } catch (err: unknown) {
      console.error('Error assigning qualification:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to assign questionnaire';
      toast.error(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign Questionnaire to Supplier</DialogTitle>
          <DialogDescription>
            Select a template and supplier to create a new questionnaire
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Template *</Label>
            <Select
              value={selectedTemplate?.toString() || ''}
              onValueChange={(value) => setSelectedTemplate(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                {offered.map((template) => (
                  <SelectItem key={template.id} value={template.id.toString()}>
                    {template.name} ({templateTypeLabel(template.template_type)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assign-supplier">Supplier *</Label>
            <SearchableSelect
              id="assign-supplier"
              options={supplierOptions}
              value={selectedSupplier?.toString() || ''}
              onChange={(value) => setSelectedSupplier(value ? parseInt(value) : null)}
              placeholder={supplierPlaceholder}
              disabled={suppliersLoading || assigning}
              inlineList
            />
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Input
              value={assignNotes}
              onChange={(e) => setAssignNotes(e.target.value)}
              placeholder="Add any notes or instructions for the supplier"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssignQualification} disabled={assigning}>
            {assigning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Assign & Send Invitation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
